import { useCallback, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import type { ChangeEvent } from 'react';
import type { QueryKey } from '@tanstack/react-query';

import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FormHelperText from '@mui/material/FormHelperText';
import Card from '@mui/material/Card';
import CardMedia from '@mui/material/CardMedia';
import CardActions from '@mui/material/CardActions';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';

import { ensureDefined } from '@e-socna/utils';
import { EMPTY_ARRAY, EMPTY_STRING } from '@e-socna/empty';
import type { Id } from '@e-socna/data-layer';

import { useFileUploaderContext } from '../../contexts/FileUploaderContext/hooks/useFileUploaderContext.hook';
import { imageCompressor } from '../../contexts/FileUploaderContext/utils/image-compressor.util';

import { EllipsisTooltip } from '../EllipsisTooltip';
import { LinkToNewTab } from '../LinkToNewTab';

interface Props {
  readonly accept?: string;
  readonly currentImageUri?: string | null;
  readonly disabled?: boolean;
  readonly error?: boolean;
  readonly fileCategoryId: Id;
  readonly fileDirectoryCategoryId: Id;
  readonly entityId: Id;
  readonly helperText?: string;
  readonly label: string;
  readonly maxSizeMb?: number;
  readonly onDelete?: () => void;
  readonly onUploadSuccess?: (fileUrl: string) => void;
  readonly queryKeysToInvalidate?: readonly QueryKey[];
  readonly showPreview?: boolean;
}

const MAX_FILE_SIZE_MB = 5;
const BYTES_PER_MB = 1_048_576;
const DELETE_MESSAGE = "Supprimer l'image";

export const ImageUploadField = ({
  accept = 'image/*',
  currentImageUri,
  disabled = false,
  error = false,
  fileCategoryId,
  fileDirectoryCategoryId,
  entityId,
  helperText,
  label,
  maxSizeMb = MAX_FILE_SIZE_MB,
  onDelete,
  onUploadSuccess,
  queryKeysToInvalidate = EMPTY_ARRAY,
  showPreview = true,
}: Readonly<Props>) => {
  const { fileUpload } = useFileUploaderContext();
  const { enqueueSnackbar } = useSnackbar();
  const [isUploading, setIsUploading] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const validateFile = useCallback(
    (file: File): string | null => {
      // Check file size
      const maxSizeBytes = maxSizeMb * BYTES_PER_MB;
      if (file.size > maxSizeBytes) {
        return `Le fichier est trop volumineux (max ${String(maxSizeMb)} Mo)`;
      }

      // Check file type
      if (!file.type.startsWith('image/')) {
        return 'Le fichier doit être une image';
      }

      return null;
    },
    [maxSizeMb],
  );

  const handleFileChange = useCallback(
    (event: Readonly<ChangeEvent<HTMLInputElement>>) => {
      const files = event.target.files;

      if (!files?.length) {
        return;
      }

      const { file } = ensureDefined(
        { file: files[0] },
        'File must be defined',
      );

      // Validate file
      const error = validateFile(file);
      if (error) {
        setValidationError(error);
        enqueueSnackbar(error, { variant: 'error' });
        return;
      }

      setValidationError(null);
      setIsUploading(true);

      void fileUpload({
        compressor: imageCompressor,
        file,
        fileCategoryId,
        fileDirectoryCategoryId,
        entityId,
        queryKeysToInvalidate,
      })
        .then(({ fileUrl }) => {
          onUploadSuccess?.(fileUrl);
          enqueueSnackbar('Image uploadée avec succès', { variant: 'success' });
        })
        .catch((error: unknown) => {
          console.error('Error uploading file:', error);
          const errorMessage =
            error instanceof Error
              ? error.message
              : "Erreur lors de l'upload de l'image";
          enqueueSnackbar(errorMessage, { variant: 'error' });
        })
        .finally(() => {
          setIsUploading(false);

          // eslint-disable-next-line functional/no-conditional-statements
          if (inputRef.current) {
            // eslint-disable-next-line functional/immutable-data
            inputRef.current.value = EMPTY_STRING;
          }
        });
    },
    [
      validateFile,
      fileUpload,
      fileCategoryId,
      fileDirectoryCategoryId,
      entityId,
      queryKeysToInvalidate,
      onUploadSuccess,
      enqueueSnackbar,
    ],
  );

  const handleDelete = useCallback(() => {
    setValidationError(null);
    onDelete?.();
  }, [onDelete]);

  const link = (
    <EllipsisTooltip title={currentImageUri ?? EMPTY_STRING}>
      <LinkToNewTab
        href={currentImageUri}
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          // whiteSpace: 'nowrap', (had to disable this in order to show the new tab link icon)
        }}
      />
    </EllipsisTooltip>
  );

  const deleteButton = (
    <Tooltip title={DELETE_MESSAGE}>
      <IconButton
        aria-label={DELETE_MESSAGE}
        color="error"
        disabled={disabled}
        onClick={handleDelete}
        size="small"
      >
        <DeleteIcon />
      </IconButton>
    </Tooltip>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <Typography gutterBottom variant="subtitle2">
        {label}
      </Typography>

      {!!(showPreview && currentImageUri) && (
        <Card sx={{ mb: 2, maxWidth: 400 }}>
          <CardMedia
            alt={label}
            component="img"
            image={currentImageUri}
            sx={{ maxHeight: 200, objectFit: 'contain', p: 2 }}
          />
          <CardActions sx={{ justifyContent: 'space-between' }}>
            {link}
            {!!onDelete && deleteButton}
          </CardActions>
        </Card>
      )}

      {!showPreview && !!currentImageUri && (
        <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption">Image actuelle : {link}</Typography>
          {!!onDelete && deleteButton}
        </Box>
      )}

      <Button
        component="label"
        disabled={disabled || isUploading}
        role={undefined}
        startIcon={
          isUploading ? <CircularProgress size={20} /> : <CloudUploadIcon />
        }
        variant="contained"
      >
        {isUploading
          ? 'Upload en cours...'
          : currentImageUri
            ? `Modifier ${label.toLowerCase()}`
            : `Télécharger ${label.toLowerCase()}`}
        <input
          accept={accept}
          disabled={disabled || isUploading}
          hidden
          onChange={handleFileChange}
          ref={inputRef}
          type="file"
        />
      </Button>

      {!!validationError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {validationError}
        </Alert>
      )}

      {!!(error && helperText) && (
        <FormHelperText error={error}>
          <Typography variant="caption">{helperText}</Typography>
        </FormHelperText>
      )}

      <Typography sx={{ mt: 1 }} variant="caption">
        Formats acceptés : images (JPG, PNG, etc.) • Taille max :{' '}
        {String(maxSizeMb)} Mo
      </Typography>
    </Box>
  );
};
